import React, { useContext } from 'react'
import styled, { css } from 'styled-components'
import { StaticQuery, graphql } from 'gatsby'
import { Link } from '../components/utils/SmartLink'
import { MobileNavigationButton, MobileMenuContext } from './MobileNavigation'

const SurrogateLink = ({ visualType, ...props }) => <Link {...props} />

const TopLink = styled(SurrogateLink)`
  ${props => props.visualType === 'normal-text-link' && css`
    color: white;
    padding: .5rem;
    text-decoration:none;
    :hover{
      background: hsla(0,0%,100%,.7);
      cursor: pointer;
      text-decoration: underline;
      color: rgba(0,0,0,.8);
    }
  `}

  ${props => props.visualType === 'rounded-button-link' && css`
    background: #fffc63;
    border-radius: 2rem;
    color: #000;
    font-weight: bolder;
    margin: .5rem;
    padding: .5rem 1rem;
    text-decoration: none;
    white-space: nowrap;
    :hover{
      background: white;
      cursor: pointer;
      color: rgba(0,0,0,.8);
    }
  `}
`

const BottomLink = styled(SurrogateLink)`
  color: rgba(0,0,0,.75);
  padding: .5rem 1rem;
  margin: 1px;
  text-decoration:none;
  text-align: center;
  flex-grow: 0;
  :hover{
    background: hsla(0,0%,100%,.7);
    cursor: pointer;
    text-decoration: underline;
  }
`

const LogoLink = styled(Link)`
  padding: .5rem;
  text-align: left;
  color: white;
  text-decoration: none;
  white-space:pre;
  line-height:1.1;
`

const LogoImageUrl = '/img/logo-small-transparent-white.png'

const LogoImage = styled(Link)`
  background-image:url("${LogoImageUrl}");
  background-size: contain;
  height: 3rem;
  width: 4rem;
  flex-shrink:0;
  flex-grow:0;
  margin-left: .5rem;
  background-position: center;
  background-repeat: no-repeat;
`

const SectionLabel = styled.div`
  align-items: center;
  background: hsla(0,0%,100%,.5);
  border-bottom: 1px solid rgba(0,0,0,.2);
  display: flex;
  font-size: 1.25em;
  justify-content: center;
  min-height: 3em;
  padding: .5rem;
  text-align: center;
`

const Spacer = styled.div`
  flex-grow:1;
`

const StyledBottomNavigation = styled.div`
  display:flex;
  justify-content:center;
  flex-shrink:0;
  background-color: ${props => props.theme.bottomNavigationColor};
  font-family: ${props => props.theme.headerFontFamily};
`

const StyledTopNavigation = styled.div`
  background-color: ${props => props.theme.topNavigationColor};
  display:flex;
  flex-direction: row;
  align-items: center;
  flex-shrink:0;
  font-family: ${props => props.theme.headerFontFamily};
  ${MobileNavigationButton}{
    display:none;
  }
  @media screen and (max-width:${props => props.theme.pageBreakPointSmall}) {
    justify-content:center;
    ${TopLink}{
      display:none;
    }
    ${MobileNavigationButton}{
      display:block;
    }
    /* ${Spacer}{
      display:none;
    } */
    /* ${LogoLink}{
      text-align:center;
    } */
  }
`

const StyledSection = styled.div`
  background: hsla(0,0%,100%,.4);
  border: 1px solid rgba(0,0,0,.2);
  box-shadow: 0 0 2em rgba(0,0,0,.2);
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  flex-shrink: 0;
  margin: 1rem;
  width: 14rem;
  flex-shrink: 1;
  margin: 1rem 1rem 0;
  max-width: 20rem;
`

const BottomNavWidthLimiter = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: stretch;
  flex-grow: 1;
  flex-shrink: 1;
  margin-bottom: 1rem;
  max-width: 64rem;
  width: 100%;
`

const LinkList = ({ links, element, container }) => {
  const LinkElem = element || Link
  const list = links.map(({ href, linkLabel, visualType }) => <LinkElem key={href} to={href} visualType={visualType}>{linkLabel}</LinkElem>)
  const Container = container

  if (Container) {
    return (
      <Container>
        { list }
      </Container>
    )
  } else {
    return (
      <div>{list}</div>
    )
  }
}

const Section = ({ section }) => {
  const { label, links } = section
  return (
    <StyledSection>
      <SectionLabel>{label}</SectionLabel>
      <LinkList links={links} element={BottomLink} container={React.Fragment} />
    </StyledSection>
  )
}

const SectionList = ({ sections }) => sections.map(section => <Section key={section.label} section={section} />)

const TopNavigation = ({ links, siteTitle }) => {
  const [state, setState] = useContext(MobileMenuContext)

  const clickHandler = () => {
    setState({
      isMenuOpen: false
    })
  }

  return (
    <StyledTopNavigation>
      <LogoImage to={'/'} onClick={clickHandler} />
      <LogoLink to={'/'} onClick={clickHandler}>{ siteTitle }</LogoLink>
      <Spacer />
      <LinkList links={links} element={TopLink} />
      <MobileNavigationButton />
    </StyledTopNavigation>
  )
}

const BottomNavigation = ({ sections }) => {
  return (
    <StyledBottomNavigation>
      <BottomNavWidthLimiter>
        <SectionList sections={sections} />
      </BottomNavWidthLimiter>
    </StyledBottomNavigation>
  )
}

export const Navigation = ({ type, data }) => {
  const query = graphql`
    {
      siteSettings:allFile(filter:{name:{eq:"siteSettings"}}){
        edges {
          node {
            childMarkdownRemark{
              frontmatter{
                headerAndFooter{
                  siteTitle
                }
              }
            }
          }
        }
      }
      navigationSettings:allFile(filter: {name: {eq: "navigationSettings"}}) {
        edges {
          node {
            childMarkdownRemark {
              frontmatter {
                topNavigation {
                  links {
                    href
                    linkLabel
                    visualType
                  }
                }
                bottomNavigation {
                  sections {
                    label
                    links {
                      href
                      linkLabel
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  `

  const render = ({ navigationSettings, siteSettings }) => {
    const { siteTitle } = siteSettings.edges[0].node.childMarkdownRemark.frontmatter.headerAndFooter
    const { bottomNavigation, topNavigation } = navigationSettings.edges[0].node.childMarkdownRemark.frontmatter
    if (type === 'top') {
      return <TopNavigation siteTitle={siteTitle} links={topNavigation.links} />
    } else if (type === 'bottom') {
      return <BottomNavigation sections={bottomNavigation.sections} />
    }
  }

  return <StaticQuery query={query} render={render} />
}
